/**
 * Normalized Vehicle Market source types for the unified Admin grid.
 * Legacy vehicle_source rows may still store uppercase enums in the database;
 * the catalog API normalizes values at the boundary.
 */

export const VEHICLE_MARKET_SOURCE_DOMAIN = "vehicle_market";

export const VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES = Object.freeze({
  MARKETPLACE: "marketplace",
  DEALER: "dealer",
  REFERENCE: "reference",
  REPAIR_REFERENCE: "repair_reference",
  MANUFACTURER: "manufacturer",
  NEW_VEHICLE_PRICE_SOURCE: "new_vehicle_price_source",
  FLEET: "fleet",
  RENTAL_RESALE: "rental_resale",
  PARTNER_FEED: "partner_feed",
  UNKNOWN: "unknown",
});

export const VEHICLE_MARKET_SOURCE_TYPE_LABELS = Object.freeze({
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.MARKETPLACE]: "Marketplace",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.DEALER]: "Dealer",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.REFERENCE]: "Reference",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.REPAIR_REFERENCE]: "Repair reference",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.MANUFACTURER]: "Manufacturer",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.NEW_VEHICLE_PRICE_SOURCE]: "New vehicle price",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.FLEET]: "Fleet",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.RENTAL_RESALE]: "Rental resale",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.PARTNER_FEED]: "Partner feed",
  [VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.UNKNOWN]: "Unknown",
});

const LEGACY_VEHICLE_SOURCE_TYPE_ALIASES = Object.freeze({
  MARKETPLACE: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.MARKETPLACE,
  DEALER: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.DEALER,
  BRAND_DEALER: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.DEALER,
  MANUFACTURER: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.MANUFACTURER,
  NEW_VEHICLE_PRICE_SOURCE: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.NEW_VEHICLE_PRICE_SOURCE,
  FLEET_RESALE: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.FLEET,
  BANK_RESALE: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.RENTAL_RESALE,
  PARTNER_FEED: VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.PARTNER_FEED,
});

/** Normalize inconsistent legacy source type values for display/filtering. */
export function normalizeVehicleMarketSourceType(value) {
  if (!value) return VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.UNKNOWN;

  const raw = String(value).trim();
  const lowered = raw.toLowerCase();

  if (Object.values(VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES).includes(lowered)) {
    return lowered;
  }

  const upper = raw.toUpperCase();
  if (LEGACY_VEHICLE_SOURCE_TYPE_ALIASES[upper]) {
    return LEGACY_VEHICLE_SOURCE_TYPE_ALIASES[upper];
  }

  if (lowered === "official") return VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.REFERENCE;
  if (lowered === "partner_dealer_feed") {
    return VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.PARTNER_FEED;
  }

  return VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.UNKNOWN;
}

export function formatVehicleMarketSourceTypeLabel(sourceTypeOrRow) {
  if (typeof sourceTypeOrRow === "object" && sourceTypeOrRow?.sourceTypeLabel) {
    return sourceTypeOrRow.sourceTypeLabel;
  }

  const sourceType =
    typeof sourceTypeOrRow === "object"
      ? normalizeVehicleMarketSourceType(sourceTypeOrRow?.sourceType)
      : normalizeVehicleMarketSourceType(sourceTypeOrRow);

  return (
    VEHICLE_MARKET_SOURCE_TYPE_LABELS[sourceType] ||
    VEHICLE_MARKET_SOURCE_TYPE_LABELS[VEHICLE_MARKET_NORMALIZED_SOURCE_TYPES.UNKNOWN]
  );
}

/** Map legacy Admin filter domains to the unified Vehicle Market domain. */
export function resolveVehicleMarketSourceDomain(domain) {
  if (!domain || domain === "all") return domain;
  if (domain === "automotive" || domain === "vehicle") return VEHICLE_MARKET_SOURCE_DOMAIN;
  return domain;
}
